import moment from "moment";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { SnackbarKey, useSnackbar } from "notistack";
// Material
import { Tooltip, Box, Typography, Grid, Paper } from "@mui/material";
// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { usePositionsWithChangeRequests } from "@/Washouts/hooks/common/usePositionsWithChangeRequests";
// Components
import { SnackbarsContent, SnackbarsActions } from "@/Layouts/components/Snackbars";
import CommissionCycleClosingDialog from "../CommissionCycleClosingDialog";
import RequestReviewButton from "../../RequestReviewButton";
// Helpers
import { getDisabledMessage } from "@/Washouts/helpers/PayrollClosing/checkReviewConditions";
// API
import { updateCommissionCycleStatus, updateCommissionCycleToFinalizingProcess } from "@/Washouts/api/PayrollClosing/mutations";
//Types
import { IUpdateCommissionCycleStatus } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingSection";
import { getAccessPosition } from "@/Washouts/helpers/WashoutReview";
import useAccessPositions from "@/Washouts/hooks/WashoutReview/useAccessPositions";
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCommissionCycleFinalizingProcessSubscription } from "@/Common/hooks/CommissionCycle";
import { useSelectionPayrollClosing } from "@/Washouts/context/selectionPayrollClosingContext";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { or } from "@/Common/helpers/sonar";

const renderSnackbarsActions = (key: SnackbarKey) => {
	return <SnackbarsActions key={key.toString()} index={key} />;
};

type CommissionCycleClosingApprovalsActionsProps = {
	commissionCycleId: string;
	payPeriodId: string;
	closeCommissionButtonVisible: boolean;
	partnerPayPeriod?: IPayPeriod | null;
};
const isCCInFinalizingProcess = (commissionCycleToVerify?: ICommissionCycle): boolean => {
	if (!commissionCycleToVerify || commissionCycleToVerify?.status === "FINALIZED") {
		return false;
	}

	if (!commissionCycleToVerify?.inFinalizingProcess || commissionCycleToVerify?.status !== "POSTED") {
		return false;
	}

	return (
		commissionCycleToVerify?.status === "POSTED" &&
		commissionCycleToVerify?.inFinalizingProcess &&
		!moment().isAfter(moment(commissionCycleToVerify?.inFinalizingProcess).add(10, "minutes"))
	);
};

const validateConditionsToCloseCommissionCycle = (commissionCycle?: ICommissionCycle): { canClose: boolean; message: string } => {
	if (!commissionCycle) {
		return { canClose: false, message: "" };
	}
	const allPayPeriods = commissionCycle?.payPeriods ?? [];
	const allPayPeriodsCompleted = allPayPeriods.every((period) => period.status === "COMPLETED");
	const commissionCycleFinalizingProcess = isCCInFinalizingProcess(commissionCycle);
	// Define conditions and their corresponding tooltip messages
	const closeCommissionConditions = [
		{
			condition: commissionCycle?.status === "FINALIZED",
			tooltip: "",
		},
		{
			condition: commissionCycle?.status !== "POSTED",
			tooltip: "Commission Cycle not in POSTED status",
		},
		{
			condition: !moment().isAfter(moment(commissionCycle?.dataUpdatedAt).add(1, "days")),
			tooltip: "24 hours have not yet passed since the last update of the Commission Cycle finalize requirements",
		},
		{
			condition: !allPayPeriodsCompleted,
			tooltip: "Some PayPeriods are not in COMPLETED status",
		},
		{
			condition: commissionCycleFinalizingProcess,
			tooltip: "Commission Cycle is being Finalized",
		},
	];

	const failingCondition = closeCommissionConditions.find((item) => item.condition);
	const closeCommissionTooltipText = failingCondition ? failingCondition.tooltip : "";
	const closeCommissionButtonEnabled = !failingCondition; // Button is enabled if no conditions fail
	return { canClose: closeCommissionButtonEnabled, message: closeCommissionTooltipText };
};

export default function CommissionCycleClosingApprovalsActions(props: Readonly<CommissionCycleClosingApprovalsActionsProps>) {
	const { commissionCycleId, payPeriodId, closeCommissionButtonVisible } = props;
	const accessPositions = useAccessPositions();
	const { enqueueSnackbar } = useSnackbar();
	const { locationId, commissionCycle, payPeriod, positionStatus, partnerPayPeriod } = useCommissionCyclesData({
		commissionCycleId,
		payPeriodId,
	});
	const allPayPeriods = commissionCycle?.payPeriods ?? [];

	const { disabledByChangeRequests = [] } = usePositionsWithChangeRequests({
		payPeriodId,
	});

	// Get the current user ID from store
	const currentUserId = useSelector((state: RootState) => {
		const userState = state.user;
		return userState?.user?._id ?? null;
	});

	const requestReviewTooltipText = getDisabledMessage({
		commissionCycle,
		payPeriod,
		payPeriods: allPayPeriods,
	});

	const [openPopup, setOpenPopup] = useState(false);
	const [commissionCycleFinalizingProcess, setCommissionCycleFinalizingProcess] = useState<boolean>(() =>
		isCCInFinalizingProcess(commissionCycle)
	);
	const [closeCommissionTooltipText, setCloseCommissionTooltipText] = useState<string>("");
	const [closeCommissionButtonEnabled, setCloseCommissionButtonEnabled] = useState<boolean>(false);
	const [isOtherUserFinalizing, setIsOtherUserFinalizing] = useState<boolean>(false);
	const [isCurrentUserFinalizing, setIsCurrentUserFinalizing] = useState<boolean>(false);

	const { mutateAsync: updateCycleStatus, isPending: requesting } = useMutation({
		mutationFn: (input: IUpdateCommissionCycleStatus) =>
			updateCommissionCycleStatus({
				locationId,
				commissionCycleId: input.commissionCycleId,
				payPeriodId: input.payPeriodId,
				status: input.status,
				positionStatus: input.positionStatus,
			}),
		onSuccess: () => {
			if (closeCommissionButtonVisible && commissionCycleFinalizingProcess) {
				enqueueSnackbar(<SnackbarsContent message={"Commission Cycle is finalized."} />, {
					variant: "success",
					preventDuplicate: true,
					action: renderSnackbarsActions,
				});
			}
		},
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred requesting the review."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: renderSnackbarsActions,
			});
		},
	});

	const { mutateAsync: updateCycleToFinalizingProcess, isPending: isPendingFinalizingProcess } = useMutation({
		mutationFn: (ccId: string) => updateCommissionCycleToFinalizingProcess(locationId, ccId, currentUserId ?? ""),
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred updating the finalizing process."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: renderSnackbarsActions,
			});
		},
	});

	const onCloseCommission = async () => {
		setCommissionCycleFinalizingProcess(true);
		const ccId = payPeriod?.previousCCId ?? commissionCycle?._id ?? "";
		const input: IUpdateCommissionCycleStatus = {
			commissionCycleId: ccId,
			payPeriodId: payPeriod?._id ?? "",
			status: "FINALIZED",
			positionStatus: [],
		};
		await updateCycleToFinalizingProcess(ccId);
		await updateCycleStatus(input);
		setOpenPopup(false);
	};

	const disabledByAccess = positionStatus
		?.filter((item) => {
			const positionAccess = getAccessPosition(accessPositions, item.position);
			return !positionAccess?.access;
		})
		.map((item) => item.position);

	const disabledPositions = [...new Set([...disabledByChangeRequests, ...disabledByAccess])];

	const partnerPositionStatus = partnerPayPeriod?.positionStatus ?? [];

	const disabledPartnerByAccess = partnerPositionStatus
		?.filter((item) => {
			const positionAccess = getAccessPosition(accessPositions, item.position);
			return !positionAccess?.access;
		})
		.map((item) => item.position);

	useCommissionCycleFinalizingProcessSubscription(commissionCycle ?? ({} as ICommissionCycle), locationId);
	const { selectedCommissionCycle } = useSelectionPayrollClosing();

	useEffect(() => {
		// Determine the first failing condition and its tooltip
		const { canClose, message } = validateConditionsToCloseCommissionCycle(commissionCycle);
		setCloseCommissionTooltipText(message);
		setCloseCommissionButtonEnabled(canClose); // Button is enabled if no conditions fail
		setCommissionCycleFinalizingProcess(isCCInFinalizingProcess(commissionCycle) || isCCInFinalizingProcess(selectedCommissionCycle));

		setIsCurrentUserFinalizing(commissionCycleFinalizingProcess && commissionCycle?.finalizingByUserId === currentUserId);
		setIsOtherUserFinalizing(commissionCycleFinalizingProcess && commissionCycle?.finalizingByUserId !== currentUserId);

		return () => {};
	}, [locationId, commissionCycleId, commissionCycle?.status, selectedCommissionCycle]);

	const isEnableLoadingFinalizedCCBtn = or(
		commissionCycleFinalizingProcess && isCurrentUserFinalizing,
		isPendingFinalizingProcess,
		closeCommissionButtonEnabled && requesting
	);

	return (
		<Grid item xs={12} sx={{ zIndex: 9999 }}>
			<Grid container className="flex-row justify-between items-start">
				<Grid item>
					<Typography className="page-title" component="div">
						{"Approvals"}
					</Typography>
				</Grid>
				<Grid item className="pr-0">
					<Grid container className="flex-row justify-between" spacing={6}>
						<Grid item>
							{requestReviewTooltipText ? (
								<Tooltip
									title={requestReviewTooltipText}
									arrow
									componentsProps={{
										tooltip: {
											sx: {
												fontFamily: "PTSansProNarrow, sans-serif",
												fontSize: 14,
											},
										},
									}}
								>
									<Box className="min-w-[151px] bg-disabled rounded-[6px] h-[30px] text-center p-[6px] pt-[5px]">
										<Typography className="select-plan-dialog-assign-btn-text text-[14px]">Request Review</Typography>
									</Box>
								</Tooltip>
							) : (
								<RequestReviewButton
									requesting={requesting}
									commissionCycle={commissionCycle}
									payPeriod={payPeriod}
									partnerPayPeriod={partnerPayPeriod}
									disabledPositions={disabledPositions}
									disabledPartnerPositions={disabledPartnerByAccess}
									positionStatuses={positionStatus}
									updateCycleStatus={updateCycleStatus}
								/>
							)}
						</Grid>
						{closeCommissionButtonVisible && (
							<Grid item>
								<CommissionCycleClosingDialog
									openModal={openPopup}
									onCloseDialog={() => setOpenPopup(false)}
									onCloseCommission={onCloseCommission}
								/>
								<Tooltip
									title={closeCommissionTooltipText}
									arrow
									componentsProps={{
										tooltip: {
											sx: {
												fontFamily: "PTSansProNarrow, sans-serif",
												fontSize: 14,
											},
										},
									}}
								>
									<Paper elevation={0}>
										<LoadingButton
											loading={isEnableLoadingFinalizedCCBtn}
											variant="contained"
											sx={{ minWidth: 151 }}
											className={
												"h-[30px] !bg-gray-blue !rounded-md !shadow-none hover:!shadow-gray-blue [&.Mui-disabled]:!bg-disabled"
											}
											disabled={!closeCommissionButtonEnabled}
											onClick={() => setOpenPopup(true)}
										>
											{(() => {
												if (isOtherUserFinalizing) {
													return (
														<Typography className="close-commission-btn-text">
															Finalize Commission Cycle
														</Typography>
													);
												}

												const isNotFinalizing = !commissionCycleFinalizingProcess && !isCurrentUserFinalizing;
												const isNotPending = !isPendingFinalizingProcess || !requesting;
												const shouldShowButton = isNotFinalizing && isNotPending;

												if (shouldShowButton) {
													return (
														<Typography className="close-commission-btn-text">
															Finalize Commission Cycle
														</Typography>
													);
												}

												return null;
											})()}
										</LoadingButton>
									</Paper>
								</Tooltip>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
